import { Box, Button, SxProps, Typography, useTheme } from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { GetServerSideProps, GetServerSidePropsContext, NextPage } from 'next';
import { QueryClient, dehydrate } from '@tanstack/react-query';
import React, { useCallback } from 'react';

import { CheckGlobalSsrErrorsOrFail } from '@/helpers/check-global-ssr-errors';

import Link from 'next/link';
import { OfferExpireTime } from '@/components/offers/offer-expire-time';
import Seo from '@/components/seo/seo';

import { STATIC_OFFER, ValidationTypes } from '@/constants/offers';
import getLangHeaders from '@/logic/helpers/get-lang-headers';
import { pxToRem } from '@/theme/typography';
import shallow from 'zustand/shallow';
import { useCity } from '@/logic/hooks';
import { useClearBooking } from '@/logic/hooks/use-clear-booking';
import { useDialog } from '@/logic/state';
import { useGetOfferDetailsBySlug } from '@/logic/services/offers';
import { useRouter } from 'next/router';
import ImageBoxFuture from '@/components/shared/image-box-future';
import loadIntlMessages from '@/logic/helpers/loadIntlMessages';

const clockBlackIcon = '/icons/offers/clock-black-icon.svg';

const chooseMovieBtnSx: SxProps = {
  fontSize: { table: pxToRem(16), mobile: '16px' },
  width: { tablet: pxToRem(404), mobile: '288px' },
  height: { tablet: pxToRem(56), mobile: '56px' },
  borderRadius: { tablet: pxToRem(16), mobile: '16px' },
  fontWeight: 700,
};

export const OfferDetails: NextPage<{
  serverCityId: number;
  path: string;
}> = ({ serverCityId, path }) => {
  const theme = useTheme();
  const intl = useIntl();
  const router = useRouter();
  const { cityId: clientCityId } = useCity();
  const { query, pathname } = useRouter();
  const setDialog = useDialog(s => s.setDialog, shallow);
  const { clearBooking } = useClearBooking();

  const cityId = +router.query.cityId || clientCityId || serverCityId;

  const filmSlug = query.film as string;

  const { data: offerDetails } = useGetOfferDetailsBySlug({
    query: { slug: query.slug as string },
    params: { cityId },
  });

  const isStaticOffer = offerDetails?.offer?.category === STATIC_OFFER;

  const handleChooseMovieClick = useCallback(() => {
    const offer = offerDetails?.offer;
    if (!offer?.isAvailableInCity) {
      setDialog({
        wrapper: true,
        isNotAvailableOfferInfoDialog: true,
        notAvailableOfferInfoDialogPayload: {
          img: offer?.logoUrl,
          logoUrlAlt: offer?.logoUrlAlt,
          bankName: offer?.bankName,
          title: offer?.title,
          validationType: offer?.validationType,
          hint: offer?.hint,
        },
      });

      return;
    }

    if (filmSlug) {
      clearBooking();
      setDialog({
        wrapper: true,
        isApplyOfferDialog: true,
        applyOfferDialogPayload: {
          offer: offerDetails?.offer,
          movieSlug: filmSlug,
        },
      });
    }
  }, [clearBooking, filmSlug, offerDetails?.offer, setDialog]);

  return (
    <>
      <Seo
        title={offerDetails?.offer?.titleTag || offerDetails?.offer?.title}
        canonical={offerDetails?.offer?.canonicalURL}
        pagePath={path}
        description={
          offerDetails?.offer?.descriptionTag || offerDetails?.offer?.description
        }
        hasFollow={offerDetails?.offer?.hasFollow}
        hasIndex={offerDetails?.offer?.hasIndex}
        image={offerDetails?.offer?.coverUrl}
      />
      <Box
        component='div'
        sx={{
          flexGrow: 1,
          mb: pxToRem(122),
          '& h1,h2,h3,h4,h5': {
            fontWeight: 700,
          },
        }}
      >
        <Typography
          component='h1'
          sx={{
            fontSize: { tablet: pxToRem(30), mobile: '18px' },
            color: theme.palette.grey[80],
            my: pxToRem(52),
            fontWeight: 700,
            px: {
              laptop: pxToRem(128),
              tablet: pxToRem(60),
              mobile: '18px',
            },

            [theme.breakpoints.down('tablet')]: {
              mt: '32px',
              mb: '24px',
            },
          }}
        >
          <FormattedMessage id='offers.offer_details' />
        </Typography>

        <Box component='div' sx={{}}>
          {offerDetails?.offer?.coverUrl && (
            <Box
              component='section'
              sx={{
                position: 'relative',
                [theme.breakpoints.up('laptop')]: {
                  '::after': {
                    background:
                      ' linear-gradient(0deg, rgba(23, 26, 31, 0.86) 0%, rgba(23, 26, 31, 0.7) 30.73%, rgba(23, 26, 31, 0) 100%)',
                    position: 'absolute',
                    bottom: 0,
                    width: '100%',
                    height: { tablet: pxToRem(458), mobile: '458px' },
                    content: "''",
                  },
                },
              }}
            >
              <ImageBoxFuture
                img={offerDetails?.offer?.coverUrl}
                title={offerDetails?.offer?.coverUrlAlt || offerDetails?.offer?.title}
                layout='intrinsic'
                objectFit='contain'
                width='1920px'
                height='1080px'
                sx={{
                  marginBottom: {
                    mobile: '16px',
                    tablet: pxToRem(32),
                  },
                }}
              />
            </Box>
          )}
          <Box
            component='section'
            sx={{
              px: {
                laptop: pxToRem(128),
                tablet: pxToRem(60),
                mobile: '16px',
              },
              mb: pxToRem(48),
            }}
          >
            <Box component='div'>
              <Typography
                component='h2'
                sx={{
                  fontSize: { tablet: pxToRem(24), mobile: '18px' },
                  color: theme.palette.grey[90],
                  mb: { tablet: pxToRem(16), mobile: '16px' },
                }}
              >
                {offerDetails?.offer?.validationType === ValidationTypes.BIN
                  ? `${offerDetails?.offer?.bankName} - ${offerDetails?.offer?.title}`
                  : offerDetails?.offer?.title}
              </Typography>
              <Typography
                component='div'
                sx={{
                  fontSize: { tablet: pxToRem(24), mobile: '16px' },
                  color: theme.palette.grey[50],
                  mb: { tablet: pxToRem(16), mobile: '8px' },
                  lineHeight: '150%',
                  whiteSpace: 'pre-wrap',
                }}
              >
                {offerDetails?.offer?.description}
              </Typography>

              {offerDetails?.offer?.endAt && (
                <Typography
                  component='time'
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    fontSize: { tablet: pxToRem(16), mobile: '13px' },
                    color: theme.palette.grey[90],
                    lineHeight: { tablet: pxToRem(24), mobile: '19px' },
                    mb: { tablet: pxToRem(42), mobile: '27px' },
                    fontWeight: 400,
                  }}
                >
                  <ImageBoxFuture
                    img={clockBlackIcon}
                    title={intl.formatMessage({
                      id: 'alt.time_icon',
                    })}
                    objectFit='cover'
                    sx={{
                      mr: '4px',
                      width: pxToRem(24),
                      height: pxToRem(24),
                    }}
                  />
                  <FormattedMessage id='my_credit_cards.expiry_date' />
                  <OfferExpireTime time={offerDetails?.offer?.endAt} />
                </Typography>
              )}

              {offerDetails?.offer?.termsConditions && (
                <Typography
                  component='h3'
                  sx={{
                    fontSize: { tablet: pxToRem(24), mobile: '16px' },
                    fontWeight: 'bold',
                    color: theme.palette.primary.main,
                    mb: { tablet: pxToRem(16), mobile: '16px' },
                  }}
                >
                  <FormattedMessage id='terms.and.conditions' />
                </Typography>
              )}
              <Box
                component='p'
                dangerouslySetInnerHTML={{
                  __html: offerDetails?.offer?.termsConditions as string,
                }}
                sx={{
                  color: theme.palette.grey[50],
                  maxWidth: '1096px',
                  mb: { tablet: pxToRem(44), mobile: '32px' },
                  lineHeight: '150%',
                  whiteSpace: 'pre-line',
                  pl: '8px',
                  [theme.breakpoints.down('tablet')]: {
                    fontSize: '16px !important',
                    '& span,p,a,q,div,b ,strong ,i ,em ,mark ,small,del,ins ,sub,sup': {
                      fontSize: '16px !important',
                    },
                    '& h1 ,h2': {
                      fontSize: '18px !important',
                      '& span': {
                        fontSize: '18px !important',
                      },
                    },
                    '& h3 ,h4': {
                      fontSize: '17px !important',
                      '& span': {
                        fontSize: '17px !important',
                      },
                    },
                  },
                }}
              />
              <Box component='div' sx={{ textAlign: 'center' }}>
                {!isStaticOffer &&
                (!offerDetails?.offer?.isAvailableInCity || filmSlug) ? (
                  <Button
                    id='select-offer'
                    variant='contained'
                    sx={chooseMovieBtnSx}
                    onClick={handleChooseMovieClick}
                  >
                    {filmSlug ? (
                      <FormattedMessage id='offers.select_offer' />
                    ) : (
                      <FormattedMessage id='offers.choose_movie' />
                    )}
                  </Button>
                ) : (
                  !isStaticOffer && (
                    <Link
                      href={{
                        pathname: pathname + '/shows',
                        query,
                      }}
                      legacyBehavior
        passHref
                      prefetch={false}
                    >
                      <Button variant='contained' sx={chooseMovieBtnSx}>
                        <FormattedMessage id='offers.choose_movie' />
                      </Button>
                    </Link>
                  )
                )}
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async (
  context: GetServerSidePropsContext
) => {
  const headers = {
    ...getLangHeaders(context.locale),
  };
  const { locale, defaultLocale } = context;
  const cityId = Number(context.query.cityId || context.req.cookies.city);

  const queryClient = new QueryClient();

  try {
    await useGetOfferDetailsBySlug.fetchQuery(queryClient, {
      query: { slug: context.query.slug as string },
      params: { cityId },
      headers,
    });
  } catch (error) {
    return CheckGlobalSsrErrorsOrFail(error);
  }

  return {
    props: {
      dehydratedState: dehydrate(queryClient),
      serverCityId: cityId,
      path: `${context.locale}${context.resolvedUrl}`,
      intlMessages: await loadIntlMessages(locale as string, defaultLocale),
    },
  };
};

export default OfferDetails;
